// src/pages/AdherentsList.jsx
import React, { useEffect, useState } from "react";
import {
  getAdherents,
  getAdherent,
  deleteAdherent,
  createAdherent,
  updateAdherent,
  importAdherentsCsv,
} from "../api/api";
import BootstrapTable from "../components/Table";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import AdherentModal from "../components/AdherentModal";
import AdherentEditModal from "../components/AdherentEditModal"; // nouveau
import { useNotifications } from "../components/NotificationsContext";

export default function AdherentsList() {
  const [showModal, setShowModal] = useState(false); // pour création
  const [showEditModal, setShowEditModal] = useState(false); // pour édition
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileAdherent, setProfileAdherent] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [list, setList] = useState([]);
  const [currentPage, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { addNotification } = useNotifications();

  const columns = [
    { Header: "ID", accessor: "id" },
    { Header: "Prenom", accessor: "first_name" },
    { Header: "Nom", accessor: "last_name" },
    { Header: "Matricule", accessor: "matricule" },
    { Header: "CIN", accessor: "cin" },
    { Header: "Telephone", accessor: "telephone" },
  ];

  const csvTemplate =
    "matricule;cin;password;first_name;last_name;telephone;email;date_naissance;situation_familiale;quota_max;fonction;gare;district;is_amical_admin;pere_nom;pere_prenom;pere_date_naissance;pere_situation;mere_nom;mere_prenom;mere_date_naissance;mere_situation;enfant1_nom;enfant1_prenom;enfant1_sexe;enfant1_date_naissance;enfant1_classe;enfant1_etablissement;enfant2_nom;enfant2_prenom;enfant2_sexe;enfant2_date_naissance;enfant2_classe;enfant2_etablissement\n" +
    "10000004;01000004;password123;Amor;Boudabbous;20000004;amor@example.com;1985-05-20;marie;1000.00;Agent;Tunis;Nord;0;Boudabbous;Mohamed;1955-03-12;vivant;Trabelsi;Fatma;1960-08-01;vivant;Youssef;Boudabbous;M;2012-04-18;7eme;College Amicale;Meriem;Boudabbous;F;2016-09-09;3eme;Ecole Amicale\n";

  const downloadCsvTemplate = () => {
    const blob = new Blob([csvTemplate], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modele-import-adherents.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const fetchAdherents = async () => {
      try {
        const res = await getAdherents();
        setList(res.data);
      } catch (err) {
        console.error("Erreur chargement adhérents :", err);
        addNotification({
          type: "error",
          title: "Erreur",
          message:
            "Impossible de charger la liste des adhérents. Réessayez plus tard.",
          delay: 6000,
        });
      }
    };

    fetchAdherents();
  }, [addNotification]);

  const handleDelete = async (row) => {
    if (!row || !row.id) {
      addNotification({
        type: "warning",
        title: "Attention",
        message: "Aucune donnée pour cette ligne.",
      });
      return;
    }

    if (!window.confirm(`Supprimer l'adhérent ${row.matricule} ?`)) return;

    try {
      await deleteAdherent(row.id);
      setList((prev) => prev.filter((a) => a.id !== row.id));

      addNotification({
        type: "success",
        title: "Suppression",
        message: `Adhérent ${row.matricule} supprimé avec succès.`,
        delay: 4000,
      });
    } catch (err) {
      console.error("Erreur de suppression :", err);
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Échec de la suppression de l’adhérent.",
        delay: 6000,
      });
    }
  };

  // Ouvrir la modal d'édition avec les données de la ligne
  const handleEdit = (row) => {
    setSelectedAdherent(row);
    setShowEditModal(true);
  };

  const handleRowClick = async (row) => {
    if (!row?.id) return;
    setShowProfile(true);
    setProfileAdherent(row);
    setProfileLoading(true);

    try {
      const res = await getAdherent(row.id);
      setProfileAdherent(res.data);
    } catch (err) {
      addNotification({
        type: "error",
        title: "Profil",
        message: "Impossible de charger la fiche complete de l'adherent.",
        delay: 6000,
      });
    } finally {
      setProfileLoading(false);
    }
  };

  // Création (déjà OK)
  const handleSave = async (data) => {
    try {
      const formData = {
        matricule: data.matricule,
        cin: data.cin,
        telephone: data.telephone,
        situation_familiale: data.situation_familiale,
        fonction: data.fonction ? parseInt(data.fonction, 10) : null,
        gare: data.gare ? parseInt(data.gare, 10) : null,
        district: data.district ? parseInt(data.district, 10) : null,
        first_name: data.first_name,
        last_name: data.last_name,
        password: data.password,
        is_amical_admin: data.is_amical_admin || false,
        parents: data.parents || [],
        enfants: data.enfants || [],
      };

      await createAdherent(formData);

      const refreshed = await getAdherents();
      setList(refreshed.data);

      setShowModal(false);
    } catch (err) {
      console.error("Erreur lors de la création :", err.response?.data || err);
      // on relance l'erreur pour que la modal affiche la notif d'erreur
      throw err;
    }
  };

  // Mise à jour complète via la modal d'édition
  // src/pages/AdherentsList.jsx

  // ✅ BULLETPROOF handleUpdate - NO undefined/null
  const handleUpdate = async (id, data) => {
    try {
      // ✅ Get CURRENT adherent to preserve existing values
      const currentAdherent = await getAdherent(id);

      const cleanData = {};

      // ✅ SAFE basic fields - fallback to current values
      cleanData.matricule = data.matricule || currentAdherent.data.matricule;
      cleanData.cin = data.cin !== undefined ? (data.cin || null) : currentAdherent.data.cin;
      cleanData.telephone = data.telephone || currentAdherent.data.telephone;
      cleanData.first_name = data.first_name || currentAdherent.data.first_name;
      cleanData.last_name = data.last_name || currentAdherent.data.last_name;
      cleanData.email = data.email !== undefined ? (data.email || '') : currentAdherent.data.email;
      cleanData.situation_familiale = data.situation_familiale || currentAdherent.data.situation_familiale;

      // ✅ SAFE FKs
      if (data.fonction) cleanData.fonction = parseInt(data.fonction);
      if (data.gare) cleanData.gare = parseInt(data.gare);
      if (data.district) cleanData.district = parseInt(data.district);

      cleanData.is_amical_admin = data.is_amical_admin !== undefined ? Boolean(data.is_amical_admin) : currentAdherent.data.is_amical_admin;

      // ✅ Password
      if (data.password) cleanData.password = data.password;
      await updateAdherent(id, cleanData);

      // Refresh list
      const refreshed = await getAdherents();
      setList(refreshed.data);

      setShowEditModal(false);
      setSelectedAdherent(null);

      addNotification({
        type: "success",
        title: "Succès",
        message: "Adhérent mis à jour!",
        delay: 4000,
      });
    } catch (err) {
      console.error('❌ Update Error:', err.response?.data || err);
      const errorMsg = err.response?.data?.detail ||
        err.response?.data?.matricule?.[0] ||
        err.response?.data?.cin?.[0] ||
        err.response?.data?.telephone?.[0] ||
        'Mise à jour échouée';
      addNotification({
        type: "error",
        title: "Erreur",
        message: errorMsg,
        delay: 7000,
      });
      throw err;
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) {
      addNotification({
        type: "warning",
        title: "CSV",
        message: "Choisis un fichier CSV avant d'importer.",
      });
      return;
    }

    setImporting(true);
    setImportResult(null);
    try {
      const res = await importAdherentsCsv(csvFile);
      setImportResult(res.data);
      const refreshed = await getAdherents();
      setList(refreshed.data);
      addNotification({
        type: "success",
        title: "Import termine",
        message: `${res.data.created} cree(s), ${res.data.updated} mis a jour.`,
        delay: 6000,
      });
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur import",
        message: err.response?.data?.detail || "Import CSV echoue.",
        delay: 8000,
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Liste des Adhérents</h4>
        <div className="d-flex gap-2 flex-wrap justify-content-end">
          <Button variant="outline-primary" onClick={downloadCsvTemplate}>
            Modele CSV
          </Button>
          <Button variant="outline-success" onClick={() => setShowImport(true)}>
            Importer CSV
          </Button>
          <Button variant="success" onClick={() => setShowModal(true)}>
            Ajouter un adherent
          </Button>
        </div>
      </div>

      {/* Modal de création */}
      <AdherentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSave}
      />

      {/* Modal d'édition */}
      <AdherentEditModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedAdherent(null);
        }}
        adherent={selectedAdherent}
        onUpdate={handleUpdate}
      />

      <AdherentProfileModal
        show={showProfile}
        loading={profileLoading}
        adherent={profileAdherent}
        onHide={() => {
          setShowProfile(false);
          setProfileAdherent(null);
        }}
        onEdit={(adherent) => {
          setShowProfile(false);
          handleEdit(adherent);
        }}
      />

      <Modal show={showImport} onHide={() => setShowImport(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Importer des adherents par CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="light">
            <strong>Separateur accepte:</strong> point-virgule <code>;</code> ou virgule <code>,</code>. Google Sheets exporte souvent avec virgule.
            <div className="mt-2">
              Colonnes adhÃ©rent: <code>matricule;cin;password;first_name;last_name;telephone;email;date_naissance;situation_familiale;quota_max;fonction;gare;district;is_amical_admin</code>
            </div>
            <div className="mt-2">
              Parents dans la mÃªme ligne: <code>pere_nom;pere_prenom;mere_nom;mere_prenom</code> avec <code>_date_naissance</code> et <code>_situation</code>.
            </div>
            <div className="mt-2">
              Enfants dans la mÃªme ligne: <code>enfant1_nom;enfant1_prenom;enfant1_sexe;enfant1_date_naissance;enfant1_classe;enfant1_etablissement</code>. Tu peux ajouter <code>enfant2_</code>, <code>enfant3_</code>, etc.
            </div>
            <div className="mt-2">
              Date: <code>YYYY-MM-DD</code>. Situation adhÃ©rent: <code>celibataire</code>, <code>marie</code>, <code>divorce</code>. Situation parent: <code>vivant</code> ou <code>decede</code>. Sexe enfant: <code>M</code> ou <code>F</code>.
            </div>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Fichier CSV</Form.Label>
            <Form.Control
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            />
          </Form.Group>

          {importResult && (
            <Alert variant={importResult.errors?.length ? "warning" : "success"}>
              <div><strong>Crees:</strong> {importResult.created}</div>
              <div><strong>Mis a jour:</strong> {importResult.updated}</div>
              <div><strong>Parents importes:</strong> {importResult.parents_imported || 0}</div>
              <div><strong>Enfants importes:</strong> {importResult.children_imported || 0}</div>
              {importResult.errors?.length > 0 && (
                <div className="mt-2">
                  <strong>Erreurs:</strong>
                  <ul className="mb-0">
                    {importResult.errors.slice(0, 10).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={downloadCsvTemplate}>
            Telecharger modele
          </Button>
          <Button variant="secondary" onClick={() => setShowImport(false)}>
            Fermer
          </Button>
          <Button variant="primary" onClick={handleCsvImport} disabled={importing}>
            {importing ? "Import..." : "Importer"}
          </Button>
        </Modal.Footer>
      </Modal>

      <BootstrapTable
        data={list}
        columns={columns}
        onEdit={handleEdit}   // ouvre maintenant la modal complète
        onDelete={handleDelete}
        currentPage={currentPage}
        perPage={perPage}
        setPage={setPage}
        setPerPage={setPerPage}
        onRowClick={handleRowClick}
      />
    </div>
  );
}

function AdherentProfileModal({ show, loading, adherent, onHide, onEdit }) {
  const fullName = `${adherent?.first_name || ""} ${adherent?.last_name || ""}`.trim();
  const parents = adherent?.parents || [];
  const enfants = adherent?.enfants || [];
  const father = parents.find((parent) => parent.type_parent === "pere");
  const mother = parents.find((parent) => parent.type_parent === "mere");

  const display = (value, fallback = "Non renseigne") => {
    if (value === null || value === undefined || value === "") return fallback;
    return value;
  };

  const statusLabel = {
    celibataire: "Celibataire",
    marie: "Marie(e)",
    divorce: "Divorce(e)",
    veuf: "Veuf/Veuve",
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="adherent-profile-modal__header">
        <div>
          <Modal.Title>Fiche adherent</Modal.Title>
          <p className="mb-0 text-muted">Recap complet pour comprendre le dossier rapidement.</p>
        </div>
      </Modal.Header>

      <Modal.Body className="adherent-profile-modal">
        {loading && !adherent ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : !adherent ? (
          <Alert variant="light">Aucun adherent selectionne.</Alert>
        ) : (
          <>
            <section className="adherent-profile-hero">
              <div className="adherent-profile-avatar">
                {(adherent.first_name?.[0] || "A").toUpperCase()}
                {(adherent.last_name?.[0] || "").toUpperCase()}
              </div>
              <div className="flex-grow-1">
                <div className="d-flex gap-2 flex-wrap align-items-center mb-2">
                  <h3 className="mb-0">{fullName || "Adherent sans nom"}</h3>
                  {adherent.is_amical_admin && <Badge bg="success">Admin amicale</Badge>}
                  {adherent.is_staff && <Badge bg="secondary">Staff</Badge>}
                </div>
                <div className="adherent-profile-hero__meta">
                  <span>Matricule {display(adherent.matricule)}</span>
                  <span>CIN {display(adherent.cin)}</span>
                  <span>Telephone {display(adherent.telephone)}</span>
                  <span>Quota {display(adherent.quota_max)} DT</span>
                </div>
              </div>
            </section>

            {loading && (
              <Alert variant="light" className="mt-3 mb-0">
                Chargement des details...
              </Alert>
            )}

            <Row className="g-3 mt-1">
              <Col lg={4}>
                <InfoCard title="Identite">
                  <InfoLine label="Prenom" value={adherent.first_name} />
                  <InfoLine label="Nom" value={adherent.last_name} />
                  <InfoLine label="Email" value={adherent.email} />
                  <InfoLine label="CIN" value={adherent.cin} />
                  <InfoLine label="Date naissance" value={adherent.date_naissance} />
                  <InfoLine
                    label="Situation familiale"
                    value={statusLabel[adherent.situation_familiale] || adherent.situation_familiale}
                  />
                </InfoCard>
              </Col>

              <Col lg={4}>
                <InfoCard title="Affectation">
                  <InfoLine label="Fonction" value={adherent.fonction_nom} />
                  <InfoLine label="Gare" value={adherent.gare_nom} />
                  <InfoLine label="District" value={adherent.district_nom} />
                  <InfoLine label="Role" value={adherent.is_amical_admin ? "Administrateur" : "Adherent"} />
                </InfoCard>
              </Col>

              <Col lg={4}>
                <InfoCard title="Famille">
                  <InfoLine label="Parents" value={`${parents.length} fiche(s)`} />
                  <InfoLine label="Enfants" value={`${enfants.length} fiche(s)`} />
                  <InfoLine label="Pere" value={father ? `${father.prenom} ${father.nom}` : ""} />
                  <InfoLine label="Mere" value={mother ? `${mother.prenom} ${mother.nom}` : ""} />
                </InfoCard>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col lg={6}>
                <InfoCard title="Parents">
                  {parents.length === 0 ? (
                    <p className="text-muted mb-0">Aucun parent enregistre.</p>
                  ) : (
                    parents.map((parent) => (
                      <FamilyRow
                        key={parent.id || `${parent.type_parent}-${parent.nom}`}
                        title={parent.type_parent === "pere" ? "Pere" : "Mere"}
                        name={`${parent.prenom || ""} ${parent.nom || ""}`.trim()}
                        meta={[
                          parent.date_naissance ? `Ne(e) le ${parent.date_naissance}` : "Date non renseignee",
                          parent.age ? `${parent.age} ans` : null,
                        ]}
                        badge={parent.situation === "decede" ? "Decede" : "Vivant"}
                        tone={parent.situation === "decede" ? "danger" : "success"}
                      />
                    ))
                  )}
                </InfoCard>
              </Col>

              <Col lg={6}>
                <InfoCard title="Enfants">
                  {enfants.length === 0 ? (
                    <p className="text-muted mb-0">Aucun enfant enregistre.</p>
                  ) : (
                    enfants.map((enfant) => (
                      <FamilyRow
                        key={enfant.id || `${enfant.nom}-${enfant.prenom}`}
                        title={enfant.sexe === "F" ? "Fille" : "Garcon"}
                        name={`${enfant.prenom || ""} ${enfant.nom || ""}`.trim()}
                        meta={[
                          enfant.date_naissance ? `Ne(e) le ${enfant.date_naissance}` : "Date non renseignee",
                          enfant.age ? `${enfant.age} ans` : null,
                          enfant.classe,
                          enfant.etablissement,
                        ]}
                        badge={enfant.sexe || "N/A"}
                        tone={enfant.sexe === "F" ? "danger" : "primary"}
                      />
                    ))
                  )}
                </InfoCard>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
        {adherent && (
          <Button variant="primary" onClick={() => onEdit(adherent)}>
            Modifier cette fiche
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

function InfoCard({ title, children }) {
  return (
    <Card className="adherent-profile-card h-100">
      <Card.Body>
        <h5>{title}</h5>
        {children}
      </Card.Body>
    </Card>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="adherent-info-line">
      <span>{label}</span>
      <strong>{value || "Non renseigne"}</strong>
    </div>
  );
}

function FamilyRow({ title, name, meta, badge, tone }) {
  return (
    <div className="family-row">
      <div>
        <div className="family-row__title">{title}</div>
        <strong>{name || "Nom non renseigne"}</strong>
        <div className="family-row__meta">
          {meta.filter(Boolean).map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </div>
      <Badge bg={tone}>{badge}</Badge>
    </div>
  );
}
